import axios from 'axios';
import { useEffect, useState } from 'react';

import { Check, CheckCircleOutline } from '@mui/icons-material';
import { CircularProgress, Drawer } from '@mui/material';

import { validTokenExists } from '../firebaseHelper';
import './Notes.css';
import './NoteReport.css';

const PREVIEW_INFO_ISSUES = [
    'Speaker incorrect',
    'Generated title incorrect',
    'Harmful content',
    'Something else',
];

const REPORT_ISSUES_BY_CONTENT_TYPE = {
    message: [
        'Transcript error',
        'Speaker incorrect',
        'Harmful content',
        'Something else',
    ],
    note: [
        "I just don't like it",
        "It's spam",
        'Hate speech or symbols',
        'Bullying or harassment',
        'False information',
        'Violence or dangerous organizations',
        'Scam or fraud',
        'Something else',
    ],
    podcast: [
        'Show image incorrect',
        'Show details incorrect',
        'Harmful content',
        'Something else',
    ],
    preview: [
        'Transcript error',
        'Speaker incorrect',
        'Generated title not relevant to highlight',
        'Starting point for preview incorrect',
        'Harmful content',
        'Something else',
    ],
    previewInfo: PREVIEW_INFO_ISSUES,
    speaker: [
        'Speaker image incorrect',
        'Speaker details incorrect',
        'Something else',
    ],
    track: [
        'Transcript error',
        'Speaker incorrect',
        'Generated section not accurate',
        'Harmful content',
        'Something else',
    ],
};

const PreviewReportIssueDrawer = ({
    open,
    onClose,
    onTransitionExited,
    preview,
    contentType = 'previewInfo',
    contentId = null,
    playerSeconds = 0,
    issues,
}) => {
    const reportIssues = issues || REPORT_ISSUES_BY_CONTENT_TYPE[contentType] || [
        'Harmful content',
        'Something else',
    ];
    const reportContentId = contentId || preview?.id;
    const isNoteReport = contentType === 'note';
    const [selectedIssues, setSelectedIssues] = useState(new Set());
    const [moreDetails, setMoreDetails] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [reporting, setReporting] = useState(false);
    const [reportSuccess, setReportSuccess] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }

        setSelectedIssues(new Set());
        setMoreDetails('');
        setErrorMessage('');
        setReporting(false);
        setReportSuccess(false);
    }, [open]);

    useEffect(() => {
        if (!reportSuccess) {
            return undefined;
        }

        const timer = setTimeout(() => {
            onClose();
        }, 1500);

        return () => clearTimeout(timer);
    }, [onClose, reportSuccess]);

    const toggleIssue = (issue) => {
        setErrorMessage('');
        setSelectedIssues((currentIssues) => {
            const nextIssues = new Set(currentIssues);
            if (nextIssues.has(issue)) {
                nextIssues.delete(issue);
            } else {
                nextIssues.add(issue);
            }
            return nextIssues;
        });
    };

    const submitReport = () => {
        const details = moreDetails.trim();
        if (selectedIssues.size === 0) {
            setErrorMessage('Select at least one issue.');
            return;
        }
        if (selectedIssues.has('Something else') && !details) {
            setErrorMessage('Please provide more details for "Something else".');
            return;
        }

        setReporting(true);
        setErrorMessage('');
        validTokenExists()
            .then(() => axios.post('/report_issue', {
                contentType,
                contentId: reportContentId,
                issues: Array.from(selectedIssues),
                moreDetails: details,
                playerSeconds,
            }))
            .then((res) => {
                if (res.data?.error) {
                    setErrorMessage(res.data.error);
                    setReporting(false);
                    return;
                }
                setReporting(false);
                setReportSuccess(true);
            })
            .catch((err) => {
                console.log(err);
                setErrorMessage(err?.response?.data?.error || err?.response?.data?.general || 'Unable to report issue.');
                setReporting(false);
            });
    };

    return (
        <Drawer
            anchor='bottom'
            open={open}
            onClose={onClose}
            ModalProps={{ onTransitionExited }}
            PaperProps={{
                className: isNoteReport ? 'notesDesign notesReportPaper' : undefined,
                sx: {
                    borderRadius: '18px 18px 0px 0px',
                    maxHeight: 'calc(var(--app-drawer-height, 100vh) * 0.9)',
                },
            }}
        >
            <div className={`previewReportDrawer appDrawerSurface${isNoteReport ? ' notesReportSurface' : ''}`}>
                <div className='previewPreferenceDragIndicator' />
                {reportSuccess ? (
                    <div className='previewReportSuccess'>
                        <CheckCircleOutline className='previewReportSuccessIcon' />
                        <div>Thank you!</div>
                    </div>
                ) : (
                    <>
                        <div className='previewReportHeader'>Report Issue</div>
                        <div className='previewReportIssueList'>
                            {reportIssues.map((issue) => {
                                const selected = selectedIssues.has(issue);
                                return (
                                    <button
                                        key={issue}
                                        type='button'
                                        className='previewReportIssueButton'
                                        aria-pressed={selected}
                                        onClick={() => toggleIssue(issue)}
                                    >
                                        <span className={['previewReportIssueCheck', selected ? 'previewReportIssueCheckSelected' : ''].filter(Boolean).join(' ')}>
                                            {selected && <Check className='previewReportIssueCheckIcon' />}
                                        </span>
                                        <span>{issue}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {selectedIssues.size > 0 && (
                            <div className='previewReportDetailsBlock'>
                                <label className='previewReportDetailsLabel' htmlFor='preview-report-details'>
                                    {selectedIssues.has('Something else') ? '* Required' : 'Optional'}
                                </label>
                                <textarea
                                    id='preview-report-details'
                                    className='previewReportDetailsInput'
                                    placeholder='More details...'
                                    value={moreDetails}
                                    onChange={(event) => setMoreDetails(event.target.value)}
                                />
                            </div>
                        )}

                        {errorMessage && (
                            <div className='previewPreferenceError' role='alert'>
                                {errorMessage}
                            </div>
                        )}

                        <button
                            type='button'
                            className='previewReportSubmitButton'
                            disabled={reporting}
                            onClick={submitReport}
                        >
                            {reporting ? <CircularProgress size={18} /> : 'Report Issue'}
                        </button>
                    </>
                )}
            </div>
        </Drawer>
    );
};

export default PreviewReportIssueDrawer;
